import {STATUS_MODEL} from '../../../../enums/statusEnum';
import {TABLES} from '../../../../Apis/Tables';
import {permissionObj} from '../../../../Helpers/permission';
import routes from '../../../../Routes/routes';
import {SUB_VIEW_MODEL, TABS_FOODIE} from '../../Dining/Model/Models';
import {TABS_VISITOR} from '../../Visitor/Model/Model';
import {RESERVATIONS_SUB_VIEW_MODEL, TABS_RESERVATIONS} from '../../Events/Model/Model';

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 */

/**
 * Modules where an approver only approves or denies a request.
 *
 * Changes are not requested in these modules, so the action is not offered by
 * the tracker. The status stays read for the answers that were already written
 * with it.
 */
export const MODULES_WITHOUT_CHANGES_REQUESTED = [
    TABLES.EVENT,
    TABLES.USER_INVITATION,
    TABLES.MATERIAL_CONTROL_REQUEST
];

/**
 * Tells whether the approvers of a module can request changes on a request.
 *
 * @param {string} module - Module the approval tracker is read with.
 *
 * @returns {boolean} True when changes can be requested in the module.
 */
export const canRequestChanges = (module) => !MODULES_WITHOUT_CHANGES_REQUESTED.includes(module);

export const APPROVAL_TRACKER_MODEL = {
    approvalFlowId: null,
    levels: [
        {
            approvalCondition: "",
            approvers: [
                {
                    reason: "",
                    status: "",
                    userEmail: "",
                    userId: null,
                    userName: "",
                    statusId: null,
                }
            ],
            levelCompleted: true,
            levelId: null,
            levelInProgress: true,
            levelNumber: 0,
            levelStatus: "",
            levelStatusId: 0
        }
    ],
    moduleId: null,
    overallStatus: {
        id: null,
        name: ""
    },

    toRegister: function () {
        return {
            approvalFlowId: this.approvalFlowId,
            moduleId: this.moduleId,
            overallStatus: {
                id: this.overallStatus?.id ?? null,
                name: this.overallStatus?.name ?? ""
            },
            levels: this.levels.map((level) => ({
                approvalCondition: level.approvalCondition || "",
                approvers: level.approvers.map((a) => ({
                    reason: a.reason || "",
                    status: a.status || "",
                    userEmail: a.userEmail || "",
                    userId: a.userId || null,
                    userName: a.userName || "",
                    statusId: a.statusId || null,
                })),
                levelCompleted: level.levelCompleted ?? true,
                levelId: level.levelId || null,
                levelInProgress: level.levelInProgress ?? true,
                levelNumber: level.levelNumber ?? 0,
                levelStatus: level.levelStatus || "",
                levelStatusId: level.levelStatusId ?? 0,
            })),
        };
    },

    set: function (data) {
        return {
            ...this,
            approvalFlowId: data.approvalFlowId || null,
            moduleId: data.moduleId || null,
            overallStatus: {
                id: data.overallStatus?.id ?? null,
                name: data.overallStatus?.name ?? ""
            },
            levels: Array.isArray(data.levels)
                ? data.levels.map((level) => ({
                    approvalCondition: level.approvalCondition || "",
                    approvers: Array.isArray(level.approvers)
                        ? level.approvers.map((a) => ({
                            reason: a.reason || "",
                            status: a.status || "",
                            userEmail: a.userEmail || "",
                            userId: a.userId || null,
                            userName: a.userName || "",
                            statusId: a.statusId || null,
                        }))
                        : [],
                    levelCompleted: level.levelCompleted ?? true,
                    levelId: level.levelId || null,
                    levelInProgress: level.levelInProgress ?? true,
                    levelNumber: level.levelNumber ?? 0,
                    levelStatus: level.levelStatus || "",
                    levelStatusId: level.levelStatusId ?? 0,
                }))
                : [],
        };
    }
};

/**
 * Views that open the approval tracker by click, grouped by module: the read
 * access each one needs and the tab it goes back to.
 *
 * The tracker has no permission of its own, it is a detail of these views, so
 * the read access of the origin is what grants it. A module can be reached from
 * more than one view, and each one has its own permission and its own tab, so
 * the entries are kept apart instead of sharing a single back target.
 */
export const APPROVAL_TRACKER_ORIGIN_MODEL = {
    [TABLES.FOODIE_REQUEST]: [
        {
            permission: permissionObj?.WEB_FOODIE_REQUEST_READ,
            backUrl: {
                pathname: routes.foodie.list,
                state: {
                    currentTab: TABS_FOODIE.REQUESTS.INDEX,
                    currentSubView: SUB_VIEW_MODEL.REQUEST.REQUEST
                }
            }
        }
    ],
    [TABLES.EVENT]: [
        {
            permission: permissionObj?.WEB_EVENT_READ,
            backUrl: {
                pathname: routes.event.list,
                state: {
                    currentTab: TABS_RESERVATIONS.BOOKINGS.INDEX,
                    currentSubView: RESERVATIONS_SUB_VIEW_MODEL.BOOKINGS.REQUEST
                }
            }
        }
    ],
    [TABLES.USER_INVITATION]: [
        {
            permission: permissionObj?.WEB_VISITOR_REQUEST_READ,
            backUrl: {
                pathname: routes.visitor.list,
                state: {
                    currentTab: TABS_VISITOR.REQUESTS.INDEX
                }
            }
        }
    ]
};

/**
 * The flow of a module is only visible to whoever can read that module: read
 * access to any of the views that link to it is enough, but access to a
 * different module is not. Unknown modules have no entry view, so they are
 * always denied.
 */
/**
 * Legend naming who the flow is deciding on, per module, so the approver knows
 * what they are acting upon. Interpolates `name` with the subject sent by the
 * back on the progress payload.
 */
export const APPROVAL_TRACKER_SUBJECT_LEGEND = {
    [TABLES.USER_INVITATION]: "invitation_approval_for_name",
    [TABLES.EVENT]: "reservation_approval_for_name",
    [TABLES.FOODIE_REQUEST]: "meal_request_approval_for_name",
};

export const canReadApprovalTrackerModule = (module, permission) =>
    (APPROVAL_TRACKER_ORIGIN_MODEL[module] || [])
        .some(({ permission: p }) => permission?.includes(p));

/**
 * Back target for deep-link entries (notifications), which arrive without the
 * backUrl the module views send in the location state.
 *
 * The destination tab is set from the location state, so it has to be one the
 * user can read: the origin views are tried in order and the first one the user
 * has access to wins. DEV sees every view, so it lands on the first origin.
 */
export const getApprovalTrackerBackUrl = (module, permission, DEV) => {
    const origins = APPROVAL_TRACKER_ORIGIN_MODEL[module] || [];
    const origin = DEV
        ? origins[0]
        : origins.find(({ permission: p }) => permission?.includes(p));
    return origin?.backUrl || null;
};

export const  APPROVAL_CONDITIONAL_MODEL = {
    ALL_MUST_APPROVE : "ALL_MUST_APPROVE",
    ONE_IS_ENOUGH : "ONE_IS_ENOUGH",
}

export const APPROVAL_TRACKER_DECISION_MODEL = {
    approvalFlowId: null,
    approvalLevelId: null,
    approvalStatusId: '',
    module: '',
    reason: '',
    subjectId: null,

    toRegister: function () {
        return {
            approvalFlowId: this.approvalFlowId,
            approvalLevelId: this.approvalLevelId,
            approvalStatusId: this.approvalStatusId,
            module: this.module,
            reason: this.reason,
            subjectId: this.subjectId,

        };
    },

    isValid: function () {
        return this.reason !== ""
    }
};

export const getToastMessageByStatus = (status) => {
    switch (status) {
        case STATUS_MODEL.APPROVED:
            return 'has_been_approved_successfully';
        case STATUS_MODEL.DENIED:
            return 'has_been_denied_successfully';
        case STATUS_MODEL.CHANGES_REQUESTED:
            return 'has_been_changes_successfully';
        default:
            return "has_been_created_successfully";
    }
};

export const getInfoParagraphByStatus = (status) => {
    switch (status) {
        case STATUS_MODEL.APPROVED:
            return 'you_can_leave_a_optional_comment';
        case STATUS_MODEL.DENIED:
            return 'please_provide_a_reason';
        case STATUS_MODEL.CHANGES_REQUESTED:
            return 'please_described_changes';
        default:
            return 'leave_a_comment';
    }
};


